"use client";

import { supabase } from "@/lib/supabase";
import type { DbTeam } from "@/types/db";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type CollectionRecord = {
  id: string;
  name: string;
  description: string;
  sharing: string;
  created_at: string;
  updated_at: string | null;
};

type FileRecord = {
  id: string;
  name: string;
  description: string;
  type: string;
  size: number;
  user_id: string | null;
  created_at: string;
  updated_at: string | null;
  processing_status: string | null;
  processing_stage: string | null;
  error_message: string | null;
  document_date: string | null;
  version_label: string | null;
  doc_created: string | null;
  doc_last_modified: string | null;
  revision: string | null;
};

type ProfileRow = {
  user_id: string;
  display_name: string | null;
  username: string | null;
};

type CollectionTeamRow = {
  team_id: string;
  collections: CollectionRecord | CollectionRecord[] | null;
};

type CollectionFileRow = {
  collection_id: string;
  files: FileRecord | FileRecord[] | null;
};

type TeamFileRow = {
  team_id: string;
  files: FileRecord | FileRecord[] | null;
};

export type FileManagerTreeNode = {
  id: string;
  entityId: string;
  kind: "team" | "collection" | "file";
  parentId?: string;
  teamId: string;
  collectionId?: string;
  name: string;
  description: string;
  isHome?: boolean;
  sharing?: string;
  fileType?: string;
  size?: number;
  uploadedBy?: string | null;
  ownerId?: string | null;
  createdAt?: string;
  updatedAt?: string | null;
  processingStatus?: string | null;
  processingStage?: string | null;
  errorMessage?: string | null;
  documentDate?: string | null;
  versionLabel?: string | null;
  docCreated?: string | null;
  docLastModified?: string | null;
  revision?: string | null;
  childCount: number;
  fileCount: number;
  children?: FileManagerTreeNode[];
  directFiles?: FileManagerTreeNode[];
};

export type UseFileManagerTreeReturn = {
  nodes: FileManagerTreeNode[];
  nodeIndex: Map<string, FileManagerTreeNode>;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  refreshStatuses: () => Promise<void>;
  totalCollections: number;
  totalFiles: number;
};

const collator = new Intl.Collator("en", {
  numeric: true,
  sensitivity: "base",
});

const sortByName = <T extends { name: string }>(items: T[]) =>
  [...items].sort((left, right) => collator.compare(left.name, right.name));

const normalizeRelation = <T,>(value: T | T[] | null | undefined) => {
  if (Array.isArray(value)) {
    return value[0] ?? null;
  }

  return value ?? null;
};

export function useFileManagerTree(
  teams: DbTeam[]
): UseFileManagerTreeReturn {
  const [nodes, setNodes] = useState<FileManagerTreeNode[]>([]);
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const teamIds = useMemo(() => teams.map((team) => team.id), [teams]);
  const teamKey = useMemo(
    () => [...teamIds].sort((left, right) => collator.compare(left, right)).join(","),
    [teamIds]
  );

  const refresh = useCallback(async () => {
    if (teams.length === 0) {
      setNodes([]);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Single RPC call replaces the previous 3-round PostgREST query chain.
      // The SECURITY DEFINER function bypasses per-row RLS on files/collections
      // (which caused N×3 sub-queries per file and triggered statement timeouts).
      const { data: treeData, error: treeError } = await supabase
        .rpc("get_kb_tree", { p_team_ids: teamIds });

      if (treeError) throw new Error(treeError.message);

      const tree = treeData as {
        collection_teams: CollectionTeamRow[];
        file_teams: TeamFileRow[];
        collection_files: CollectionFileRow[];
        profiles: ProfileRow[];
      };

      const collectionsByTeam = new Map<string, CollectionRecord[]>();
      const collectionIds = new Set<string>();

      for (const team of teams) {
        collectionsByTeam.set(team.id, []);
      }

      for (const row of tree.collection_teams ?? []) {
        const collection = normalizeRelation(row.collections);
        if (!collection) continue;
        collectionsByTeam.get(row.team_id)?.push(collection);
        collectionIds.add(collection.id);
      }

      const filesByTeam = new Map<string, FileRecord[]>();
      for (const team of teams) {
        filesByTeam.set(team.id, []);
      }
      for (const row of tree.file_teams ?? []) {
        const file = normalizeRelation(row.files);
        if (!file) continue;
        const current = filesByTeam.get(row.team_id) ?? [];
        if (!current.some((f) => f.id === file.id)) current.push(file);
        filesByTeam.set(row.team_id, current);
      }

      const filesByCollection = new Map<string, FileRecord[]>();

      for (const row of tree.collection_files ?? []) {
        const file = normalizeRelation(row.files);
        if (!file) continue;
        const currentFiles = filesByCollection.get(row.collection_id) ?? [];
        if (!currentFiles.some((currentFile) => currentFile.id === file.id)) {
          currentFiles.push(file);
        }
        filesByCollection.set(row.collection_id, currentFiles);
      }

      const uploaderNameById = new Map<string, string>();
      for (const row of (tree.profiles as ProfileRow[] | null) ?? []) {
        const name = row.display_name?.trim() || row.username?.trim() || null;
        if (name) uploaderNameById.set(row.user_id, name);
      }

      const resolveUploadedBy = (file: FileRecord) =>
        file.user_id ? (uploaderNameById.get(file.user_id) ?? null) : null;

      const nextNodes = teams.map<FileManagerTreeNode>((team) => {
        const teamNodeId = `team:${team.id}`;
        const collectionNodes = sortByName(
          collectionsByTeam.get(team.id) ?? []
        ).map<FileManagerTreeNode>((collection) => {
          const collectionNodeId = `collection:${team.id}:${collection.id}`;
          const fileNodes = sortByName(filesByCollection.get(collection.id) ?? []).map<FileManagerTreeNode>(
            (file) => ({
              id: `file:${team.id}:${collection.id}:${file.id}`,
              entityId: file.id,
              kind: "file" as const,
              parentId: collectionNodeId,
              teamId: team.id,
              collectionId: collection.id,
              name: file.name,
              description: file.description,
              fileType: file.type,
              size: file.size,
              uploadedBy: resolveUploadedBy(file),
              ownerId: file.user_id,
              createdAt: file.created_at,
              updatedAt: file.updated_at,
              processingStatus: file.processing_status,
              processingStage: file.processing_stage,
              errorMessage: file.error_message,
              documentDate: file.document_date,
              versionLabel: file.version_label,
              docCreated: file.doc_created,
              docLastModified: file.doc_last_modified,
              revision: file.revision,
              childCount: 0,
              fileCount: 0,
            })
          );

          return {
            id: collectionNodeId,
            entityId: collection.id,
            kind: "collection",
            parentId: teamNodeId,
            teamId: team.id,
            collectionId: collection.id,
            name: collection.name,
            description: collection.description,
            sharing: collection.sharing,
            createdAt: collection.created_at,
            updatedAt: collection.updated_at,
            childCount: fileNodes.length,
            fileCount: fileNodes.length,
            children: fileNodes,
          };
        });

        // Collection membership takes priority in the tree. Any remaining files
        // are rendered at the team root level.
        const collectionFileIdsForTeam = new Set(
          collectionNodes.flatMap((collectionNode) =>
            (collectionNode.children ?? []).map((fileNode) => fileNode.entityId)
          )
        );

        const directFileNodes = sortByName(
          (filesByTeam.get(team.id) ?? []).filter(
            (file) => !collectionFileIdsForTeam.has(file.id)
          )
        ).map<FileManagerTreeNode>((file) => ({
          id: `file:${team.id}::${file.id}`,
          entityId: file.id,
          kind: "file" as const,
          parentId: teamNodeId,
          teamId: team.id,
          name: file.name,
          description: file.description,
          fileType: file.type,
          size: file.size,
          uploadedBy: resolveUploadedBy(file),
          ownerId: file.user_id,
          createdAt: file.created_at,
          updatedAt: file.updated_at,
          processingStatus: file.processing_status,
          processingStage: file.processing_stage,
          errorMessage: file.error_message,
          documentDate: file.document_date,
          versionLabel: file.version_label,
          docCreated: file.doc_created,
          docLastModified: file.doc_last_modified,
          revision: file.revision,
          childCount: 0,
          fileCount: 0,
        }));

        return {
          id: teamNodeId,
          entityId: team.id,
          kind: "team",
          teamId: team.id,
          name: team.name,
          description: team.description,
          isHome: team.is_home,
          createdAt: team.created_at,
          updatedAt: team.updated_at,
          childCount: collectionNodes.length,
          fileCount:
            collectionNodes.reduce((total, col) => total + col.fileCount, 0) +
            directFileNodes.length,
          children: collectionNodes,
          directFiles: directFileNodes,
        };
      });

      setNodes(nextNodes);
    } catch (refreshError) {
      setError(
        refreshError instanceof Error
          ? refreshError.message
          : "Unable to load team files."
      );
      setNodes([]);
    } finally {
      setLoading(false);
    }
  }, [teamIds, teams]);

  useEffect(() => {
    void refresh();
  }, [refresh, teamKey]);

  const nodeIndex = useMemo(() => {
    const nextIndex = new Map<string, FileManagerTreeNode>();

    const addNode = (node: FileManagerTreeNode) => {
      nextIndex.set(node.id, node);
      node.children?.forEach(addNode);
      node.directFiles?.forEach(addNode);
    };

    nodes.forEach(addNode);

    return nextIndex;
  }, [nodes]);

  const totalCollections = useMemo(
    () => nodes.reduce((total, node) => total + node.childCount, 0),
    [nodes]
  );
  const totalFiles = useMemo(
    () => nodes.reduce((total, node) => total + node.fileCount, 0),
    [nodes]
  );

  const refreshStatuses = useCallback(async () => {
    const inFlightIds: string[] = [];
    const collect = (node: FileManagerTreeNode) => {
      if (
        node.kind === "file" &&
        (node.processingStatus?.toUpperCase() === "PENDING" ||
          node.processingStatus?.toUpperCase() === "PROCESSING")
      ) {
        inFlightIds.push(node.entityId);
      }
      node.children?.forEach(collect);
      node.directFiles?.forEach(collect);
    };
    nodesRef.current.forEach(collect);

    if (inFlightIds.length === 0) return;

    const { data, error: fetchError } = await supabase
      .from("files")
      .select("id, processing_status, processing_stage, error_message")
      .in("id", inFlightIds);

    if (fetchError || !data) return;

    const updates = new Map(
      data.map((row) => [
        row.id as string,
        {
          processingStatus: row.processing_status as string | null,
          processingStage: row.processing_stage as string | null,
          errorMessage: row.error_message as string | null,
        },
      ])
    );

    const patchNode = (node: FileManagerTreeNode): FileManagerTreeNode => {
      if (node.kind === "file") {
        const patch = updates.get(node.entityId);
        return patch ? { ...node, ...patch } : node;
      }
      return {
        ...node,
        children: node.children?.map(patchNode),
        directFiles: node.directFiles?.map(patchNode),
      };
    };

    setNodes((prev) => prev.map(patchNode));
  }, []);

  return {
    nodes,
    nodeIndex,
    loading,
    error,
    refresh,
    refreshStatuses,
    totalCollections,
    totalFiles,
  };
}